import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { FieldTip } from '@/components/FieldTip.js';
import { Switch } from '@/components/ui/Switch.js';

type UserRow = {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login: string | null;
};

type Page = { items: UserRow[]; total: number; page: number; limit: number };

export function MembersTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: ['admin-users', q],
    queryFn: async () =>
      (await api.get<Page>('/admin/users', { params: { q: q || undefined, limit: 50 } })).data,
  });

  const patch = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { role?: string; is_active?: boolean } }) =>
      (await api.patch(`/admin/users/${id}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  if (query.isLoading) return <p className="text-ha-muted">Carregando…</p>;

  return (
    <div>
      <div className="mb-4 max-w-md">
        <FieldTip label="Buscar" hint="Nome ou e-mail.">
          <input
            className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar…"
          />
        </FieldTip>
      </div>
      {patch.error && (
        <p className="mb-2 text-sm text-feedback">{extractError(patch.error).message}</p>
      )}
      <div className="overflow-x-auto rounded-card border border-ha-border2 bg-ha-card">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-ha-border text-left text-[10px] uppercase text-ha-muted">
              <th className="p-2">Nome</th>
              <th className="p-2">E-mail</th>
              <th className="p-2">Papel</th>
              <th className="p-2">Ativo</th>
              <th className="p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {query.data?.items.map((u) => (
              <tr key={u.id} className="border-b border-ha-border">
                <td className="p-2">{u.name}</td>
                <td className="p-2 text-ha-subtle">{u.email}</td>
                <td className="p-2">{u.role}</td>
                <td className="p-2">
                  {u.role === 'SUPER_ADMIN' ? (
                    <span className="text-ha-muted">—</span>
                  ) : (
                    <Switch
                      checked={u.is_active}
                      aria-label={`Ativo ${u.name}`}
                      onCheckedChange={(checked) =>
                        patch.mutate({ id: u.id, data: { is_active: checked } })
                      }
                    />
                  )}
                </td>
                <td className="p-2">
                  {u.role !== 'SUPER_ADMIN' && (
                    <div className="flex flex-wrap gap-2">
                      {u.role === 'MEMBER' && (
                        <button
                          type="button"
                          className="text-xs underline"
                          onClick={() => patch.mutate({ id: u.id, data: { role: 'ADMIN' } })}
                        >
                          Tornar admin
                        </button>
                      )}
                      {u.role === 'ADMIN' && (
                        <button
                          type="button"
                          className="text-xs underline"
                          onClick={() => patch.mutate({ id: u.id, data: { role: 'MEMBER' } })}
                        >
                          Remover admin
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
